/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */

import React, { useState, useEffect, StrictMode } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { GridContainer, GridRow, GridItem, gridConfig, GlobalThemeComponent } from '@tvg/fuse-design';
import appWebhook from '@sd-utility/appwebhook';
import ImageCarousel from 'components/ImageCarousel/ImageCarousel';
import ProductDetails from 'components/ProductDetails/ProductDetails';
import CreditOffers from 'components/CreditOffers';
import DeliveryAndReturns from 'components/DeliveryAndReturns';
import StockMessage from 'components/StockMessage/StockMessage';
import AddToBasket from 'components/AddToBasket/AddToBasket';
import SaveForLater from 'components/SaveForLater/SaveForLater';
import ProductDescription from 'components/Description';
import RatingsAndReviews from 'components/RatingsAndReviews/RatingsAndReviews';
import NonReturnableInfo from 'components/NonReturnableInfo/NonReturnableInfo';
import PrfInfo from 'components/PrfInfo/PrfInfo';
import OptionSelectorList from 'components/OptionSelectorList/OptionSelectorList';
import SelectedSkuProvider from 'context/SelectedSku/SelectedSkuProvider';
import DynamicPrice from 'components/DynamicPrice';
import Recommendations from 'components/Recommendations/Recommendations';
import SmartOffer from 'components/SmartOffer/SmartOffer';
import TrueFitWidget from 'components/TrueFitWidget';
import Personalization from 'components/Personalization/Personalization';
import LoadBee from 'components/LoadBee/LoadBee';
import InlineContent from 'components/OneWorldSync/InlineContent';
import EnergyLabel from 'components/OneWorldSync/EnergyLabel';
import TradeIn from 'components/TradeIn/TradeIn';
import { Insurance } from 'components/Insurance/Insurance';
import PercentageAndDeliveryVoucher from 'components/PromotionVoucher/PercentageAndDeliveryVoucher';
import InsuranceVoucher from 'components/PromotionVoucher/InsuranceVoucher';
import CustomPromotionComponent from 'components/PromotionVoucher/CustomPromotionVoucher';
import PromotionIdentifier from 'components/PromotionIdentifier/PromotionIdentifier';
import { DialogService } from 'components/DialogService/DialogService';
import { Addon } from 'components/Addon/Addon';
import AppleRichContent from 'components/AppleRichContent/AppleRichContent';
import { CustomPromotionVoucher, ProductVariant, PromotionVoucher } from 'types/Product';
import { VoucherTypes } from 'constants/promotion-voucher-constants';
import BrandTypes from 'constants/brand';
import WeeInfo from 'components/WeeInfo/WeeInfo';
import ShareButton from 'components/ShareButton/ShareButton';
import PDPGalleriesExtension from 'components/PDPGalleriesExtension/PDPGalleriesExtension';
import sanitizeProductName from 'utilities/product/sanitizeProductName';
import getSessionConfirmationNumber from 'services/session/getSessionConfirmationNumber';
import CsrfTokenError from 'utilities/logging/error-log-type/csrfTokenError';
// import { captureClientSideError } from 'utilities/util';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { SiteContext } from 'context/SiteContext';
import { FeatureToggleContext } from 'context/FeatureToggle/FeatureToggleContext';
import { Customer } from 'types/CustomerTypes';
import CustomerServiceFactory, { CustomerServiceType } from 'services/customer/factory/CustomerServiceFactory';
import { ExperimentsContext } from 'context/ExperimentsContext';
import { ContainerProps } from './ContainerProps';
import { useUIStore } from '../../store';

const ProductPageContainer = styled.div<Partial<ContainerProps>>`
  font-size: var(--font-size-m1);
  line-height: var(--font-line-height-m1);
`;

const StyledGridItem = styled(GridItem)`
  display: flex;
  flex-direction: column;
`;

const AccordionsGridItem = styled(GridItem)`
  display: flex;
  flex-direction: column-reverse;

  @media (min-width: ${gridConfig.m.minWidth}) {
    flex-direction: column;
  }
`;

const ProductDetailsContainer = styled.div`
  @media (min-width: ${gridConfig.m.minWidth}) {
    padding-left: var(--spacing-space16);
  }

  @media (min-width: ${gridConfig.l.minWidth}) {
    padding-left: var(--spacing-space24);
  }
`;

const SaveAndShareContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-global-space3);

  @media (min-width: ${gridConfig.m.minWidth}) {
    gap: var(--spacing-global-space4);
  }
`;

const InsuranceContainer = styled.div`
  padding-top: var(--spacing-space16);
  margin-bottom: var(--spacing-space16);
`;

const logger = LoggerFactory.getLogger('Container');

export default function Container(props: ContainerProps) {
  const {
    productId,
    identification,
    images,
    brand,
    name,
    price,
    lowestPrice,
    reviews,
    skus,
    description,
    BV_URL,
    returnable,
    features,
    trueFitEnabled,
    personalizableMaxLength,
    isInsuranceAvailable,
    promotionIdentifier,
    promotionVoucher,
    digitalDownload,
    insuranceContent,
    roundel,
    linkedAddonServicesContent,
    tradeInValue,
    videos,
    appleRichContentKey,
    tradingDepartmentId,
    site,
    weeeEligibilityInd,
    hasDeliveryContent,
    xMockResponseValue,
    featureToggles,
    experiments = {},
    fspProduct = false,
    enableSmartOffer,
    mockDirectives = {},
  } = props;
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [sessionConfirmationNumber, setSessionConfirmationNumber] = useState<string>('');
  const [canShare, setCanShare] = useState<any | undefined>(true);
  const [platform, setPlatform] = useState('');
  const [skuList, setSkuList] = useState<ProductVariant[]>(skus);
  const [customer, setCustomer] = useState<Customer>({});
  const pdpGalleryEnabled = experiments['se-15'];

  const productName = brand ? `${brand} ${name}` : name;

  useEffect(() => {
    const formattedskus = skus.map((sku) => ({
      sku: sku.option,
      sellingPrice: sku.price?.amount?.decimal ?? '',
    }));
    useUIStore.getState().setAttribute(formattedskus);
  }, [skus]);

  useEffect(() => {
    const { userAgent } = window.navigator;
    const isAppUserAgent = /SDAndroidApp|SDIPhoneApp_2\.0|SDIPhoneApp/.test(userAgent);
    if (userAgent.includes('SDIphoneApp') || userAgent.includes('SDIPhoneApp_2.0')) {
      setPlatform('iphone');
    } else if (userAgent.includes('SDAndroidApp')) {
      setPlatform('android');
    } else {
      setPlatform('web');
    }
    if (window.navigator.share === undefined && !isAppUserAgent) {
      setCanShare(false);
    }
  }, []);

  const mpn = skus[0].manufacturerPartNumber;

  const handleGetSessionConfirmationNumber = async () => {
    try {
      const response = await getSessionConfirmationNumber(window);

      if (response && response.sessionConfirmationNumber) {
        setSessionConfirmationNumber(response.sessionConfirmationNumber);
      }
    } catch (error) {
      (await logger).logWithCode(ErrorCodes.CSRF_TOKEN_ERROR, error);
    }
  };

  const handleSetCsrfToken = async () => {
    try {
      const csrfTokenMetaTagValue = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      if (!csrfTokenMetaTagValue) {
        throw new CsrfTokenError('CSRF token is missing');
      }

      setCsrfToken(csrfTokenMetaTagValue);
    } catch (error) {
      (await logger).logWithCode(ErrorCodes.CSRF_TOKEN_ERROR, error as Error);
    }
  };

  const handleProductView = () => {
    appWebhook('productView', {
      currencyCode: price.amount.currencyCode,
      productData: [
        {
          SKU: skus[0].option,
          catalogueID: identification.itemNumber,
          name: productName,
          quantity: 1,
          unitPrice: price.amount.decimal,
        },
      ],
    });
  };

  const stopLoadingOnApp = () => {
    appWebhook('stopLoading', true);
  };

  const customerService = CustomerServiceFactory.createCustomerService(
    featureToggles?.enableCustomerMock ? CustomerServiceType.MOCK : CustomerServiceType.ODM,
  );

  const handleGetCustomer = async () => {
    const customerResponse = await customerService.getCustomer();
    setCustomer(customerResponse);
  };

  useEffect(() => {
    stopLoadingOnApp();
    handleSetCsrfToken();
    handleGetSessionConfirmationNumber();
    handleGetCustomer();
    handleProductView();

    // Expose product data on window object
    const exposedData = {
      customer: {
        isBnplEligible: window?.universal_variable?.user?.bnplEligibility !== 'false',
      },
      item: {
        productId,
        preDiscountPrice: lowestPrice.amount.unformattedPrice,
        itemId: identification.itemNumber, // Item Number (product level, not option number)
        retailBrandId: brand,
        productTypeCode: skus[0]?.productTypeCode,
      },
    };

    // Expose the data on window object
    (window as any).smartOfferProductData = exposedData;
    (window as any).dispatchEvent(new Event('smartOfferDataReady'));
  }, []);

  const isPromotionVoucher = (voucher: any): voucher is PromotionVoucher => voucher && voucher.type !== undefined;

  const isCustomPromotionVoucher = (voucher: any): voucher is CustomPromotionVoucher =>
    voucher && voucher.dialogDescription !== undefined;

  const processPromotionType = (voucher: any): PromotionVoucher | CustomPromotionVoucher | undefined => {
    if (isPromotionVoucher(voucher)) {
      return voucher;
    }
    if (isCustomPromotionVoucher(voucher)) {
      return voucher;
    }
    return undefined;
  };
  const processedPromotionVoucher = processPromotionType(promotionVoucher);

  return (
    <StrictMode>
      <StyleSheetManager enableVendorPrefixes>
        <GlobalThemeComponent brand={site} />
        <FeatureToggleContext.Provider value={featureToggles ?? null}>
          <SiteContext.Provider value={site}>
            <ExperimentsContext.Provider value={experiments}>
              <SelectedSkuProvider
                skus={skuList}
                setSkus={setSkuList}
                isInsuranceAvailable={isInsuranceAvailable}
                productId={productId}
                productItemNumber={identification.itemNumber}
                productName={productName}
                productPrice={price.amount}
                personalizableMaxLength={personalizableMaxLength}
                digitalDownload={digitalDownload}
                insuranceContent={insuranceContent}
                linkedAddonServicesContent={linkedAddonServicesContent}
                basketMockEnabled={!!featureToggles?.enableBasketMock}
                features={features}
                mockDirectives={mockDirectives}
                experiments={experiments}
              >
                <ProductPageContainer id="product-page-container" data-testid="product">
                  <GridContainer>
                    <GridRow>
                      <StyledGridItem data-testid="carousel-promo" m={6} l={7}>
                        {promotionIdentifier && (
                          <PromotionIdentifier
                            text={promotionIdentifier.displayName}
                            colour={promotionIdentifier.colour}
                            productIdentifierId={promotionIdentifier.productIdentifierId}
                          />
                        )}
                        <ImageCarousel
                          images={images}
                          productTitle={productName}
                          roundel={roundel}
                          videos={videos}
                          site={site}
                        />
                      </StyledGridItem>

                      <StyledGridItem m={6} l={5}>
                        <ProductDetailsContainer>
                          <ProductDetails
                            brand={brand}
                            name={name}
                            price={price.amount}
                            reviewTotal={reviews.total}
                            reviewStars={reviews.stars}
                            productId={productId}
                          />
                          <EnergyLabel brand={brand} ean={skus[0].ean} mpn={mpn} option={skus[0].option} site={site} />
                          {(featureToggles?.enableSmartOffer || enableSmartOffer) && site === BrandTypes.VERY && (
                            <SmartOffer />
                          )}
                          {!(featureToggles?.enableSmartOffer || enableSmartOffer) &&
                            !processedPromotionVoucher &&
                            site !== BrandTypes.VERYIE && (
                              <CreditOffers
                                itemNumber={identification.itemNumber}
                                productName={productName}
                                productPrice={lowestPrice.amount.current}
                                productPriceDecimal={lowestPrice.amount.decimal}
                                site={site}
                                creditOffersMockEnabled={featureToggles?.enableCreditOffersMock}
                                skus={skus}
                              />
                            )}
                          {!(featureToggles?.enableSmartOffer || enableSmartOffer) && site === BrandTypes.VERY && (
                            <>
                              {processedPromotionVoucher &&
                                (isPromotionVoucher(processedPromotionVoucher) &&
                                processedPromotionVoucher.type === VoucherTypes.Insurance ? (
                                  <InsuranceVoucher promotionVoucher={processedPromotionVoucher} />
                                ) : (
                                  <PercentageAndDeliveryVoucher promotionVoucher={processedPromotionVoucher} />
                                ))}

                              {processedPromotionVoucher && isCustomPromotionVoucher(processedPromotionVoucher) && (
                                <CustomPromotionComponent promotionVoucher={processedPromotionVoucher} />
                              )}
                            </>
                          )}

                          {site !== 'veryie' && hasDeliveryContent && !digitalDownload && (
                            <DeliveryAndReturns
                              brand={brand}
                              site={site}
                              skus={skus}
                              csrfToken={csrfToken}
                              vdpMockEnabled={!!featureToggles?.enableVDPMock}
                            />
                          )}

                          {returnable && <NonReturnableInfo returnable={returnable} />}

                          {features?.length && skuList && skuList.length > 1 ? (
                            <OptionSelectorList features={features} skus={skuList} />
                          ) : null}

                          {trueFitEnabled && (
                            <TrueFitWidget
                              locale={site === 'veryie' ? 'en_IE' : 'en_GB'}
                              itemNumber={identification.itemNumber}
                              colour={skus[0]?.options?.colour}
                              userId={customer.customerID || ''}
                            />
                          )}
                          {isInsuranceAvailable && experiments['se-20'] ? (
                            <InsuranceContainer
                              data-testid="insurance-redesign-container"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html:
                                  '<esi:include src="/fragment/fspr-aux-features-fe/insurance" onerror="continue" />',
                              }}
                            />
                          ) : (
                            isInsuranceAvailable && <Insurance />
                          )}
                          <Addon />

                          {personalizableMaxLength && <Personalization maxLength={personalizableMaxLength} />}
                          {csrfToken && (
                            <StockMessage
                              csrfToken={csrfToken}
                              isFSPEnabled={fspProduct}
                              fspMockEnabled={!!featureToggles?.enableFSPMock}
                              xMockResponseValue={xMockResponseValue}
                            />
                          )}

                          <PrfInfo prfValue={price.amount.prfValue} weeeEligibilityInd={weeeEligibilityInd} />

                          <DynamicPrice price={price.amount} />
                          <AddToBasket
                            productData={{
                              price: price.amount,
                              reviewTotal: reviews.total,
                              reviewStars: reviews.stars,
                            }}
                          />
                          <SaveAndShareContainer data-testid="save-and-share-container">
                            <SaveForLater
                              sku={skus[0]}
                              csrfToken={csrfToken}
                              sessionConfirmationNumber={sessionConfirmationNumber}
                              productId={productId}
                              itemNumber={identification.itemNumber}
                            />
                            {canShare && (
                              <ShareButton
                                productName={sanitizeProductName(productName)}
                                site={site}
                                image={images[0]?.identifier}
                                platform={platform}
                              />
                            )}
                          </SaveAndShareContainer>
                          {tradeInValue && site !== BrandTypes.VERYIE && <TradeIn tradeInValue={tradeInValue} />}
                        </ProductDetailsContainer>
                      </StyledGridItem>
                    </GridRow>

                    {pdpGalleryEnabled && (
                      <GridRow>
                        <StyledGridItem>
                          <PDPGalleriesExtension />
                        </StyledGridItem>
                      </GridRow>
                    )}
                    {tradingDepartmentId && (mpn || skus[0]?.ean) ? (
                      <GridRow>
                        <AccordionsGridItem>
                          <InlineContent
                            brand={brand}
                            ean={skus[0].ean}
                            mpn={mpn}
                            option={skus[0].option}
                            Description={
                              <ProductDescription
                                itemNumber={identification.itemNumber}
                                ean={identification.ean}
                                description={description}
                                useAccordion
                              />
                            }
                            AppleContent={
                              appleRichContentKey ? <AppleRichContent contentKey={appleRichContentKey} /> : undefined
                            }
                            LoadBee={<LoadBee ean={identification.ean} />}
                            flixMediaEnabled
                          />
                        </AccordionsGridItem>
                      </GridRow>
                    ) : (
                      <GridRow>
                        <StyledGridItem>
                          <ProductDescription
                            itemNumber={identification.itemNumber}
                            ean={identification.ean}
                            description={description}
                          />
                        </StyledGridItem>

                        {appleRichContentKey && (
                          <StyledGridItem>
                            <AppleRichContent contentKey={appleRichContentKey} />
                          </StyledGridItem>
                        )}

                        <StyledGridItem>
                          <LoadBee ean={identification.ean} />
                        </StyledGridItem>

                        <StyledGridItem>
                          <InlineContent
                            brand={brand}
                            ean={skus[0].ean}
                            mpn={mpn}
                            option={skus[0].option}
                            flixMediaEnabled
                          />
                        </StyledGridItem>
                      </GridRow>
                    )}

                    <GridRow>
                      <StyledGridItem>
                        <Recommendations hideBorder={!!tradingDepartmentId} />
                      </StyledGridItem>
                    </GridRow>

                    <GridRow>
                      <StyledGridItem>
                        <RatingsAndReviews productId={productId} BV_URL={BV_URL} />
                      </StyledGridItem>
                    </GridRow>
                    {weeeEligibilityInd && (
                      <GridRow>
                        <StyledGridItem>
                          <WeeInfo />
                        </StyledGridItem>
                      </GridRow>
                    )}
                  </GridContainer>
                  <DialogService />
                </ProductPageContainer>
              </SelectedSkuProvider>
            </ExperimentsContext.Provider>
          </SiteContext.Provider>
        </FeatureToggleContext.Provider>
      </StyleSheetManager>
    </StrictMode>
  );
}
